import { useEffect, useRef, useState } from "react";
import { Instrument, InstrumentRangeType, InstrumentType } from "../../types";
import { AccountAtom } from "../../store";
import { useAtomValue } from "jotai";
import { getInstruments } from "../../api";
import { useDebounce } from "../../features/hooks";
interface UseInstrumentSearchController {
  instruments: InstrumentEx[];
  instrumentType: InstrumentType;
  setInstrumentType: (instrumentType: InstrumentType) => void;
  selectedInstrument?: Instrument;
  setSelectedInstrument: (instrument: Instrument) => void;
  search: (query: string) => void;
  loading: boolean;
}

// extending the base type with a mapping to its underlying
export interface InstrumentEx extends Instrument {
  underlyingName?: string;
}

export const useInstrumentSearchController = (
  onInstrumentSelected: (
    instrument: Instrument,
    children?: Instrument[]
  ) => void
): UseInstrumentSearchController => {
  const initialised = useRef(false);
  const [loading, setLoading] = useState(false);
  const [instrumentType, setInstrumentType] = useState<InstrumentType>(
    InstrumentType.Equity
  );
  const [instruments, setInstruments] = useState<InstrumentEx[]>([]);
  const [selectedInstrument, setSelectedInstrument] = useState<
    Instrument | undefined
  >(undefined);

  const account = useAtomValue(AccountAtom);

  const loadInstruments = async (query: string) => {
    const trimmedQuery = query.trim();
    if (trimmedQuery.length < 1) {
      setInstruments([]);
      return;
    }

    if (account.selectedAccount) {
      setLoading(true);
      try {
        const result = await getInstruments(
          account.selectedAccount.id,
          InstrumentRangeType.ALL,
          false,
          false,
          true,
          trimmedQuery,
          instrumentType
        );
        const instrumentById = new Map(
          result.map((instrument) => [instrument.id, instrument])
        );

        // mapping the underlying instrument name
        const enhancedInstruments = result.map((instrument) => {
          return {
            ...instrument,
            underlyingName: !instrument.underlyingInstrumentId
              ? instrument.name
              : (instrumentById.get(instrument.underlyingInstrumentId)
                  ?.name ?? undefined),
          };
        });

        setInstruments(enhancedInstruments);
      } finally {
        setLoading(false);
      }
    }
  };

  const search = useDebounce(loadInstruments, 350);

  const filterUnderlyings = (): InstrumentEx[] => {
    const result = instruments.filter(
      (instrument) => instrument.instrumentType === instrumentType
    );
    return result;
  };

  useEffect(() => {
    if (selectedInstrument) {
      const children = instruments.filter(
        (x) => x.underlyingInstrumentId === selectedInstrument.id
      );
      onInstrumentSelected(selectedInstrument, children);
    }
  }, [selectedInstrument]);

  useEffect(() => {
    if (initialised.current) {
      setSelectedInstrument(undefined);
      setInstruments([]);
    }
  }, [instrumentType]);

  useEffect(() => {
    if (initialised.current) return;
    initialised.current = true;
  }, [account]);

  return {
    instruments: filterUnderlyings(),
    instrumentType,
    setInstrumentType,
    selectedInstrument,
    setSelectedInstrument,
    search,
    loading,
  };
};
